import cn from "classnames";
import { useEffect, useMemo, useState } from "react";
import { useMediaQuery } from "@rshbintech.dboul/core-ui";
import { useAppTranslation } from "../../../hooks";
import { Menu } from "../../menu";
import { Header } from "../../header";
import { Content } from "../../content";
import { useClientSettings } from "../../../common/client-settings";
import { MenuItemsService } from "../../menu/menu-items-service";
import { DEFAULT_MENU_CONFIG } from "../../menu/menu-config";
import { MAIN_PAGE_PATHNAME } from "../../constants";
import { useProtectedRoutes } from "../../hooks";
import { getFirstAvailableMenuItemPath } from "./utils";
import { LayoutContentProps } from "./types";
import styles from "./styles.scss";

export const LayoutContent = ({
  clientInfo,
  delegateInfo,
  clientBlocks,
  monolithClientId,
}: LayoutContentProps) => {
  const { language } = useAppTranslation();
  const { validateRoute, validateRouteForBankForBanksUser } =
    useProtectedRoutes();

  const isTablet = useMediaQuery("tablet");
  const isDesktop = useMediaQuery("desktop");

  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const { clientSettings } = useClientSettings();

  useEffect(() => {
    if (isMenuOpen) {
      document.body.classList.add(styles.fixed);
    } else {
      document.body.classList.remove(styles.fixed);
    }
  }, [isMenuOpen]);

  useEffect(() => {
    if (isDesktop) {
      setIsMenuOpen(false);
    }
  }, [isDesktop]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const menuConfig = useMemo(
    () =>
      new MenuItemsService({
        defaultMenuConfig: DEFAULT_MENU_CONFIG,
      })
        .disableByClientSettings(clientSettings)
        .disableByAvailabilityForUser(validateRouteForBankForBanksUser)
        .disableByAvailabilityForUser(validateRoute).menuConfig,
    [validateRoute, validateRouteForBankForBanksUser, clientSettings]
  );

  const mainPagePath = useMemo(() => {
    if (validateRoute(MAIN_PAGE_PATHNAME)) {
      return MAIN_PAGE_PATHNAME;
    }

    return (
      getFirstAvailableMenuItemPath(menuConfig.top) ||
      getFirstAvailableMenuItemPath(menuConfig.middle) ||
      getFirstAvailableMenuItemPath(menuConfig.bottom) ||
      ""
    );
  }, [validateRoute, menuConfig.top, menuConfig.middle, menuConfig.bottom]);

  return (
    <>
      <Header
        mainPagePath={mainPagePath}
        userName={delegateInfo?.fullName}
        clientName={delegateInfo?.orgName}
        clientSettings={clientSettings}
        isMenuOpen={isMenuOpen}
        onHeaderIconClick={toggleMenu}
      />
      <div
        className={cn(styles.wrapper, {
          [styles.tablet]: isTablet,
        })}
      >
        <Menu
          menuConfig={menuConfig}
          isOpen={isMenuOpen}
          clientSettings={clientSettings}
          clientBlocks={clientBlocks}
          onMenuItemClick={toggleMenu}
          delegateInfo={delegateInfo}
        />
        <Content
          mainPagePath={mainPagePath}
          clientBlocks={clientBlocks}
          delegateInfo={delegateInfo}
          monolithClientId={monolithClientId}
          clientInfo={clientInfo}
          isMenuOpen={isMenuOpen}
          clientSettings={clientSettings}
          onOverlayClick={toggleMenu}
          language={language}
        />
      </div>
    </>
  );
};
